import React, { useState, useEffect } from "react";
import Form1, { Form1Data } from "./Form1";
import Form2, { Form2Data } from "./Form2";
import Form3, { Form1Data as Form3Data } from "./Form3";
import Form4, { Form2Data as Form4Data } from "./Form4";
import Form5, { Form3Data as Form5Data } from "./Form5";
import Form6, { Form4Data as Form6Data } from "./Form6";
import Form7, { Form5Data as Form7Data } from "./Form7";
import Form8, { Form6Data as Form8Data } from "./Form8";
import Form9, { Form7Data as Form9Data } from "./Form9";
import Form10, { Form8Data as Form10Data } from "./Form10";
import { kitchenService } from "../../services";
import { toast } from "react-toastify";

interface RegistrationData {
  step1?: Form1Data;
  step2?: Form2Data;
  step3?: Form3Data;
  step4?: Form4Data;
  step5?: Form5Data;
  step6?: Form6Data;
  step7?: Form7Data;
  step8?: Form8Data;
  step9?: Form9Data;
  step10?: Form10Data;
}

interface KitchenRegistrationProps {
  onComplete?: () => void;
}

const KitchenRegistration: React.FC<KitchenRegistrationProps> = ({
  onComplete,
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [registrationData, setRegistrationData] = useState<RegistrationData>(
    {},
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Fetch existing restaurant details on mount
  useEffect(() => {
    const fetchRestaurantDetails = async () => {
      const token = localStorage.getItem("kitchenAuthToken");
      const verifiedPhone = localStorage.getItem("verifiedPhoneNumber");
      
      if (!token) {
        setLoading(false);
        // Initialize history for fresh registration
        window.history.replaceState({ step: 1, isRegistration: true }, '', window.location.pathname);
        return;
      }

      // If token exists, user has completed auth, skip to step 3
      if (token && verifiedPhone) {
        setRegistrationData((prev) => ({
          ...prev,
          step1: { mobileNumber: verifiedPhone },
          step2: { mobileNumber: verifiedPhone, otp: '', token },
        }));
      }

      try {
        const response: any = await kitchenService.getRestaurantDetails();
        
        if (response.status && response.data) {
          const data = response.data;
          
          // Map API response to form data structures
          const mappedData: RegistrationData = {
            step1: {
              mobileNumber: verifiedPhone || data.primaryContactNumber || "",
            },
            step2: {
              mobileNumber: verifiedPhone || data.primaryContactNumber || "",
              otp: "",
              token: token,
            },
            step3: {
              businessName: data.businessName || "",
              ownerName: data.ownerName || "",
              businessType: data.businessType || "CLOUD_KITCHEN",
              primaryContactNumber: data.primaryContactNumber || "",
              email: data.email || "",
              brandLogo: data.brandLogo || "",
              alternateContact: data.alternateContact || "",
            },
            step4: {
              panNumber: data.panNumber || "",
              gstNumber: data.gstNumber || "",
              fssaiLicenseNumber: data.fssaiLicenseNumber || "",
              fssaiCertificate: data.fssaiCertificate || "",
              shopActLicense: data.shopActLicense || "",
            },
            step5: data.location ? {
              address: data.location.address || "",
              landmark: data.location.landmark || "",
              state: data.location.state || "",
              city: data.location.city || "",
              pincode: data.location.pincode || "",
              lat: data.location.lat || 0,
              lng: data.location.lng || 0,
              googleMapsLink: data.location.googleMapsLink || "",
            } : undefined,
            step6: {
              operatingDays: data.operatingDays || [],
              openTime: data.operatingHours?.openTime || "",
              closeTime: data.operatingHours?.closeTime || "",
              avgPreparationTime: data.avgPreparationTime || 30,
              acceptsPreOrders: data.acceptsPreOrders ?? false,
              maxOrdersPerHour: data.maxOrdersPerHour || 0,
            },
            step7: {
              deliveryAvailable: data.deliveryAvailable ?? true,
              deliveryRadius: data.deliveryRadius || 0,
              deliveryType: data.deliveryType || "BOTH",
              packagingCharges: data.packagingCharges || 0,
              minimumOrderValue: data.minimumOrderValue || 0,
            },
            step8: {
              restaurantCategory: data.restaurantCategory || 'Select Category',
              seatingCapacity: data.seatingCapacity || 0,
              indoorSeating: data.indoorSeating || 0,
              outdoorSeating: data.outdoorSeating || 0,
              acAvailable: data.acAvailable || 'NO',
              parkingAvailable: data.parkingAvailable ?? false,
              valetService: data.valetService ?? false,
              alcoholServed: data.alcoholServed ?? false,
              liveMusic: data.liveMusic ?? false,
              acceptsTableBookings: data.acceptsTableBookings ?? false,
              qrSelfOrderingEnabled: data.qrSelfOrderingEnabled ?? false,
              advanceBookingDays: data.advanceBookingDays || 0,
              cancellationPolicy: data.cancellationPolicy || 'NO_REFUND',
            },
            step9: data.bankDetails ? {
              accountHolderName: data.bankDetails.accountHolderName || '',
              bankName: data.bankDetails.bankName || 'Select Your Bank',
              accountNumber: data.bankDetails.accountNumber || '',
              ifscCode: data.bankDetails.ifscCode || '',
              upiId: data.bankDetails.upiId || '',
              settlementCycle: data.bankDetails.settlementCycle || 'Select Settlement Cycle',
            } : undefined,
            step10: {
              kitchenPhotos: data.kitchenPhotos || [],
              menuCardImages: data.menuCardImages || [],
              foodItemImages: data.foodItemImages || [],
            },
          };

          setRegistrationData(mappedData);
          
          // Set current step based on API response (step + 3 since steps 1-2 are auth)
          // API steps 0-7 map to UI steps 3-10
          const apiStep = data.step || 0;
          const nextStep = apiStep + 3;
          const finalStep = nextStep > 10 ? 10 : nextStep;
          
          console.log('[History Init] Starting history setup for step:', finalStep);
          console.log('[History Init] Current history.state before init:', window.history.state);
          
          // Initialize browser history for navigation
          // Clear any existing forward history and set up step sequence
          window.history.replaceState({ step: 1, isRegistration: true }, '', window.location.pathname);
          console.log('[History Init] After replaceState(step 1):', window.history.state);
          
          // Push history states for steps 2 through finalStep
          for (let i = 2; i <= finalStep; i++) {
            window.history.pushState({ step: i, isRegistration: true }, '', window.location.pathname);
            console.log(`[History Init] After pushState(step ${i}):`, window.history.state);
          }
          
          console.log('[History Init] Final history.state:', window.history.state);
          console.log('[History Init] History length:', window.history.length);
          
          // Set the current step AFTER setting up history
          setCurrentStep(finalStep);
        }
      } catch (err: any) {
        console.error("Failed to fetch restaurant details:", err);
        
        // Don't show toast error for "Restaurant details not found" - this is expected for new users
        const isNewUser = err.message?.includes("Restaurant details not found") || 
                          err.message?.includes("not found");
        
        if (!isNewUser) {
          // Only show error toast for actual failures (network errors, etc.)
          setError(err.message || "Failed to load saved data");
          toast.error(err.message || "Failed to load saved data");
        }
        
        // Initialize history even on error (for new users starting fresh)
        window.history.replaceState({ step: 1, isRegistration: true }, '', window.location.pathname);
      } finally {
        setLoading(false);
      }
    };

    fetchRestaurantDetails();
  }, []);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      console.log('[PopState] Event triggered, event.state:', event.state);
      console.log('[PopState] Current history.state:', window.history.state);
      
      if (event.state && event.state.step && event.state.isRegistration) {
        // Navigate to the specific form step in registration flow
        console.log('[PopState] Navigating to step:', event.state.step);
        setCurrentStep(event.state.step);
      } else {
        // No state or not registration flow - let browser handle natural navigation
        console.log('[PopState] No valid state, letting browser handle navigation');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const handleNext = (stepData: any) => {
    setRegistrationData((prev) => ({
      ...prev,
      [`step${currentStep}`]: stepData,
    }));

    if (currentStep === 10) {
      // Final step completed
      if (onComplete) {
        onComplete();
      }
    } else {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      // Push new state to browser history
      window.history.pushState({ step: nextStep, isRegistration: true }, '', window.location.pathname);
    }
  };

  const renderForm = () => {
    switch (currentStep) {
      case 1:
        return (
          <Form1 onNext={handleNext} initialData={registrationData.step1} />
        );
      case 2:
        return (
          <Form2
            onNext={handleNext}
            initialData={registrationData.step2}
            mobileNumber={registrationData.step1?.mobileNumber || ''}
            autoFillOtp={registrationData.step1?.otp}
          />
        );
      case 3:
        return (
          <Form3
            onNext={handleNext}
            initialData={registrationData.step3}
          />
        );
      case 4:
        return (
          <Form4
            onNext={handleNext}
            initialData={registrationData.step4}
          />
        );
      case 5:
        return (
          <Form5
            onNext={handleNext}
            initialData={registrationData.step5}
          />
        );
      case 6:
        return (
          <Form6
            onNext={handleNext}
            initialData={registrationData.step6}
          />
        );
      case 7:
        return (
          <Form7
            onNext={handleNext}
            initialData={registrationData.step7}
          />
        );
      case 8:
        return (
          <Form8
            onNext={handleNext}
            initialData={registrationData.step8}
          />
        );
      case 9:
        return (
          <Form9
            onNext={handleNext}
            initialData={registrationData.step9}
          />
        );
      case 10:
        return (
          <Form10
            onNext={handleNext}
            initialData={registrationData.step10}
          />
        );
      default:
        return (
          <Form1 onNext={handleNext} initialData={registrationData.step1} />
        );
    }
  };

  return (
    <div className="w-full min-h-screen bg-gray-50 p-5">
      {loading ? (
        // Loading state
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600 font-Urbanist">Loading your details...</p>
          </div>
        </div>
      ) : (
        <>
          {/* Error Message */}
          {error && (
            <div className="max-w-xl mx-auto mb-4">
              <div className="bg-yellow-50 text-yellow-700 px-4 py-3 rounded-lg text-sm">
                {error}. Starting fresh registration.
              </div>
            </div>
          )}

          {/* Progress Indicator - Only show for registration forms (steps 3-10) */}
          {currentStep > 2 && (
            <div className="max-w-xl mx-auto pt-2 flex justify-between items-center">
              <div>
                <h1 className="text-[#6A6A6A] text-[20px] font-medium mb-1 text-left leading-[25px]">
                  {currentStep === 1
                    ? "10%"
                    : currentStep === 2
                      ? "20%"
                      : currentStep === 3
                        ? "30%"
                        : currentStep === 4
                          ? "40%"
                          : currentStep === 5
                            ? "50%"
                            : currentStep === 6
                              ? "60%"
                              : currentStep === 7
                                ? "70%"
                                : currentStep === 8
                                  ? "80%"
                                  : currentStep === 9
                                    ? "90%"
                                    : currentStep === 10
                                      ? "100%"
                                      : ""}{" "}
                </h1>
              </div>
              <div className="w-[450px] h-1.5 bg-gray-200 rounded-full overflow-hidden mb-3">
                <div
                  className="h-full bg-gradient-to-r from-orange-500 to-orange-400 transition-all duration-300 ease-out"
                  style={{ width: `${(currentStep / 10) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* Form Content */}
          <div className="max-w-xl mx-auto">{renderForm()}</div>
        </>
      )}
    </div>
  );
};

export default KitchenRegistration;
